import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import api from '../services/api';

import { fmtNumber } from '../utils/format';
/**
 * Establishes a single authenticated WebSocket connection to /api/ws and
 * dispatches incoming server events to local handlers — replacing poll delay
 * for high-priority admin -> user updates.
 *
 * Events handled:
 *   - "account_status_changed" — if suspended/banned, force logout immediately
 *   - "kyc_updated"            — refetch /auth/me + show toast
 *   - "deposit_addresses_changed" — fire a window event so DepositTab can refetch
 *
 * Auto-reconnects with exponential backoff. Keep-alive ping every 25s.
 */
export const useUserWebSocket = ({ userId, token, onUserUpdate, onLogout }) => {
  const wsRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const pingIntervalRef = useRef(null);
  const closedByClient = useRef(false);

  useEffect(() => {
    if (!userId || !token) return undefined;
    closedByClient.current = false;

    const buildUrl = () => {
      const apiBase = process.env.REACT_APP_BACKEND_URL || '';
      // /api is forwarded to the FastAPI backend; preserve it on the WS path.
      const wsBase = apiBase.replace(/^http/, 'ws');
      return `${wsBase}/api/ws?token=${encodeURIComponent(token)}`;
    };

    const connect = () => {
      try {
        const ws = new WebSocket(buildUrl());
        wsRef.current = ws;

        ws.onopen = () => {
          reconnectAttempts.current = 0;
          // Keep-alive ping every 25s so K8s ingress doesn't drop idle sockets
          pingIntervalRef.current = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) ws.send('ping');
          }, 25000);
        };

        ws.onmessage = (event) => {
          let msg;
          try { msg = JSON.parse(event.data); } catch { return; }
          handleServerEvent(msg);
        };

        ws.onclose = () => {
          if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
            pingIntervalRef.current = null;
          }
          if (closedByClient.current) return;
          // Reconnect with exponential backoff capped at 30s
          const delay = Math.min(1000 * 2 ** reconnectAttempts.current, 30000);
          reconnectAttempts.current += 1;
          setTimeout(connect, delay);
        };

        ws.onerror = () => { /* ignored — onclose handles reconnect */ };
      } catch (err) {
        // Initial constructor failure: schedule retry
        const delay = Math.min(1000 * 2 ** reconnectAttempts.current, 30000);
        reconnectAttempts.current += 1;
        setTimeout(connect, delay);
      }
    };

    const handleServerEvent = async ({ event, payload }) => {
      switch (event) {
        case 'connected':
          // Initial handshake — nothing to do
          break;

        case 'account_status_changed': {
          const status = payload?.account_status;
          if (status === 'suspended' || status === 'banned') {
            toast.error(
              `Your account has been ${status}.${payload?.reason ? ' Reason: ' + payload.reason : ''}`,
              { duration: 6000 }
            );
            // Defer logout slightly so the toast renders
            setTimeout(() => onLogout?.(), 800);
          } else if (status === 'active') {
            toast.success('Your account has been re-activated.');
            try {
              const res = await api.get('/auth/me');
              onUserUpdate?.(res.data);
            } catch { /* ignore */ }
          }
          break;
        }

        case 'kyc_updated': {
          const kycStatus = payload?.kyc_status;
          if (kycStatus === 'approved') {
            toast.success(`KYC approved! You are now Level ${payload.kyc_level}.`);
          } else if (kycStatus === 'rejected') {
            toast.error('Your KYC was rejected. Check your notifications for details.');
          } else {
            toast.message('Your KYC status has been updated.');
          }
          try {
            const res = await api.get('/auth/me');
            onUserUpdate?.(res.data);
          } catch { /* ignore */ }
          break;
        }

        case 'deposit_addresses_changed':
          toast.message('Your deposit addresses have been updated.');
          // Notify any listening components (DepositTab) to refetch
          window.dispatchEvent(new CustomEvent('phantomtrades:deposit-addresses-changed', {
            detail: payload || {}
          }));
          break;

        case 'balance_updated': {
          const verb = payload?.adjustment_type === 'subtract' ? 'subtracted' : 'added';
          toast.message(
            `Admin ${verb} $${payload?.amount?.toFixed?.(2) || payload?.amount}. Balance: $${payload?.new_balance?.toFixed?.(2) || payload?.new_balance}`
          );
          window.dispatchEvent(new CustomEvent('phantomtrades:balance-changed', { detail: payload || {} }));
          break;
        }

        case 'crypto_balance_updated': {
          const sym = payload?.crypto_symbol || 'crypto';
          const verb = payload?.adjustment_type === 'subtract' ? 'subtracted' : 'added';
          toast.message(`Admin ${verb} ${fmtNumber(payload?.amount, 0, 8)} ${sym} (new: ${fmtNumber(payload?.new_amount, 0, 8)})`);
          window.dispatchEvent(new CustomEvent('phantomtrades:balance-changed', { detail: payload || {} }));
          break;
        }

        case 'deposit_processed': {
          if (payload?.status === 'approved') {
            toast.success(`Deposit approved: +${fmtNumber(payload?.amount, 0, 8)} ${payload?.crypto_type || 'USDT'}`);
          } else {
            toast.error(`Deposit rejected${payload?.admin_notes ? ': ' + payload.admin_notes : ''}`);
          }
          window.dispatchEvent(new CustomEvent('phantomtrades:deposit-processed', { detail: payload || {} }));
          window.dispatchEvent(new CustomEvent('phantomtrades:balance-changed', { detail: payload || {} }));
          break;
        }

        case 'withdrawal_processed': {
          const status = payload?.status;
          if (status === 'paid' || status === 'approved') {
            toast.success(`Withdrawal ${status}: ${fmtNumber(payload?.amount, 0, 8)} ${payload?.crypto_type || 'USDT'}`);
          } else {
            toast.error(`Withdrawal rejected${payload?.admin_notes ? ': ' + payload.admin_notes : ''}`);
          }
          window.dispatchEvent(new CustomEvent('phantomtrades:withdrawal-processed', { detail: payload || {} }));
          window.dispatchEvent(new CustomEvent('phantomtrades:balance-changed', { detail: payload || {} }));
          break;
        }

        case 'trade_settled': {
          // TradeWatcher turns this into the result report dialog
          window.dispatchEvent(new CustomEvent('phantomtrades:trade-settled', { detail: payload || {} }));
          window.dispatchEvent(new CustomEvent('phantomtrades:balance-changed', { detail: payload || {} }));
          break;
        }

        case 'notification_received':
          window.dispatchEvent(new CustomEvent('phantomtrades:notification-received', { detail: payload || {} }));
          if (payload?.title) {
            toast.message(payload.title, { description: payload.message });
          }
          break;

        case 'price_alert':
          window.dispatchEvent(new CustomEvent('phantomtrades:price-alert', { detail: payload || {} }));
          break;

        default:
          // Forward-compat: silently ignore unknown events
          break;
      }
    };

    connect();

    return () => {
      closedByClient.current = true;
      if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
      if (wsRef.current) {
        try { wsRef.current.close(); } catch { /* ignore */ }
      }
    };
  }, [userId, token, onUserUpdate, onLogout]);
};
